import { useState } from "react";
import { Check, Footprints, Link as LinkIcon, Sun, User, X } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ImageWithSkeleton } from "@/components/ui/image-with-skeleton";

import goodFraming from "@/assets/tips/good-framing.webp";
import badFraming from "@/assets/tips/bad-framing.webp";
import goodLighting from "@/assets/tips/good-lighting.webp";
import badLighting from "@/assets/tips/bad-lighting.webp";
import goodShoes from "@/assets/tips/good-shoes.webp";
import badShoes from "@/assets/tips/bad-shoes.webp";

interface Props {
  className?: string;
}

type TabId = "body" | "shoes" | "lighting";

type Example = {
  src: string;
  caption: string;
  points: string[];
};

const TABS: Array<{
  id: TabId;
  label: string;
  icon: typeof User;
  good: Example;
  bad: Example;
}> = [
  {
    id: "body",
    label: "Full body",
    icon: User,
    good: {
      src: goodFraming,
      caption: "Full body, centered",
      points: ["Head to feet visible", "Stand straight, face camera"],
    },
    bad: {
      src: badFraming,
      caption: "Cropped or tilted",
      points: ["Do not crop body", "Avoid angled selfies"],
    },
  },
  {
    id: "shoes",
    label: "Shoes",
    icon: Footprints,
    good: {
      src: goodShoes,
      caption: "Feet flat, both shoes visible",
      points: ["Show shoes fully", "Stand on plain floor"],
    },
    bad: {
      src: badShoes,
      caption: "Feet hidden or cropped",
      points: ["Do not hide one shoe", "Avoid dark busy floors"],
    },
  },
  {
    id: "lighting",
    label: "Lighting",
    icon: Sun,
    good: {
      src: goodLighting,
      caption: "Bright even light",
      points: ["Face clearly visible", "No harsh shadows"],
    },
    bad: {
      src: badLighting,
      caption: "Dark or backlit",
      points: ["Avoid dim rooms", "No strong backlight"],
    },
  },
];

const ExampleCard = ({ kind, example }: { kind: "good" | "bad"; example: Example }) => {
  const isGood = kind === "good";
  const Icon = isGood ? Check : X;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border bg-card/70 shadow-soft",
        isGood ? "border-primary/40" : "border-destructive/40",
      )}
    >
      <div className="relative aspect-[3/4] bg-muted">
        <ImageWithSkeleton src={example.src} alt={example.caption} />
        <div
          className={cn(
            "absolute left-2 top-2 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[9px] font-bold uppercase tracking-[0.18em]",
            isGood ? "bg-primary text-primary-foreground" : "bg-destructive text-destructive-foreground",
          )}
        >
          <Icon className="h-3 w-3" strokeWidth={3} />
          {isGood ? "Do" : "Don't"}
        </div>
      </div>
      <div className="p-2.5">
        <p className="text-[11px] font-semibold text-foreground">{example.caption}</p>
        <ul className="mt-1.5 space-y-1">
          {example.points.map((point) => (
            <li key={point} className="flex items-start gap-1.5 text-[10px] leading-snug text-muted-foreground">
              <Icon className={cn("mt-0.5 h-3 w-3 shrink-0", isGood ? "text-primary" : "text-destructive")} strokeWidth={3} />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export const PhotoExamples = ({ className }: Props) => {
  const [tab, setTab] = useState<TabId>("body");
  const current = TABS.find((item) => item.id === tab) ?? TABS[0];

  return (
    <div className={cn("rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-accent/10 p-3 shadow-soft", className)}>
      <div className="mb-2 flex flex-wrap gap-2">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] transition-all",
              tab === id
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background/70 text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="h-3 w-3" />
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <ExampleCard kind="good" example={current.good} />
        <ExampleCard kind="bad" example={current.bad} />
      </div>

      <Link
        to="/app/photo-tips"
        className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary hover:underline"
      >
        <LinkIcon className="h-3 w-3" />
        See full guide
      </Link>
    </div>
  );
};
